import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Headset, Trash2, ChevronRight, User } from "lucide-react";
import { RiderShell, DriverShell } from "@/components/Shells";
import { Spinner } from "@/components/States";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { timeAgo, cn } from "@/lib/format";

export default function Inbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [convos, setConvos] = useState(null);
  const Shell = user?.role === "driver" ? DriverShell : RiderShell;

  const load = () => api("/inbox").then((r) => setConvos(r.conversations || [])).catch(() => setConvos([]));
  useEffect(() => { load(); }, []);

  const startSupport = async () => {
    try { const r = await api("/support/start", { method: "POST" }); navigate(`/chat/${r.ride_id}`); }
    catch { /* noop */ }
  };

  const remove = async (rideId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation from your inbox?")) return;
    setConvos((c) => c.filter((x) => x.ride_id !== rideId));
    try { await api(`/inbox/${rideId}`, { method: "DELETE" }); } catch { /* noop */ }
  };

  return (
    <Shell>
      <div className="mx-auto max-w-2xl px-5 py-6">
        <h1 className="mb-4 text-2xl font-extrabold text-foreground">Inbox</h1>

        {/* Support card */}
        <button onClick={startSupport} data-testid="contact-support"
          className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-soft transition-colors hover:bg-secondary">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600"><Headset className="h-5 w-5 text-white" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-foreground">Getaride Support</p>
            <p className="truncate text-sm text-muted-foreground">Need help? Start a chat with our team.</p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </button>

        {convos === null ? (
          <div className="flex justify-center py-16"><Spinner className="h-7 w-7 text-brand-600" /></div>
        ) : convos.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-14 text-center">
            <MessageSquare className="h-10 w-10 text-secondary-foreground/30" />
            <p className="text-base font-semibold text-foreground">No conversations yet.</p>
            <p className="max-w-xs text-sm text-muted-foreground">Messages with your driver or rider will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {convos.map((c) => (
              <div key={c.ride_id} data-testid={`convo-${c.ride_id}`} onClick={() => navigate(`/chat/${c.ride_id}`)}
                className="group flex cursor-pointer items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft transition-colors hover:border-brand-500/50">
                <span className={cn("flex h-11 w-11 items-center justify-center rounded-full", c.is_support ? "bg-brand-600 text-white" : "bg-accent text-brand-600")}>
                  {c.is_support ? <Headset className="h-5 w-5" /> : <User className="h-5 w-5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className={cn("truncate font-semibold text-foreground", c.unread > 0 && "font-extrabold")}>{c.other_name}</p>
                    <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(c.last_at)}</span>
                  </div>
                  <p className={cn("truncate text-sm", c.unread > 0 ? "font-semibold text-foreground" : "text-muted-foreground")}>{c.last_text}</p>
                  <p className="truncate text-xs text-muted-foreground/70">{c.route}</p>
                </div>
                {c.unread > 0 && (
                  <span data-testid={`unread-${c.ride_id}`} className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 text-[11px] font-bold text-white">
                    {c.unread > 9 ? "9+" : c.unread}
                  </span>
                )}
                <button onClick={(e) => remove(c.ride_id, e)} data-testid={`delete-${c.ride_id}`} className="p-1 text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
